import dayjs from 'dayjs';

/**
 * Attempts to retrieve a given education period from either an array of options or an object mapping educationPeriod ID value
 * to the education period object.
 * @param educationPeriodCode {int} - The integer education period code that represents the chosen education period
 * @param educationPeriods {object | list} - All known education periods in the format id -> education period map or an array
 * @returns {object | null} The chosen education period
 */
const retrieveEducationPeriod = (educationPeriodCode, educationPeriods) => {
  if (!educationPeriods) {
    return null;
  }

  if (Array.isArray(educationPeriods)) {
    return educationPeriods.filter((p) => p.id === educationPeriodCode)[0];
  }
  return educationPeriods[educationPeriodCode];
};

/**
 * Determines whether the provided `dateToCheck` is within the window of availability for the
 * provided education period. Returns true if the date is a valid date for the provided education period,
 * false if it is too early or too late.
 * @param educationPeriod {int} - The numeric index that represents the education period (i.e. 22 for 2020-2021)
 * @param educationPeriodOptions {object | list} - All known education periods in the format id -> education period map or an array
 * @param dateToCheck {string | number} - Either string or dayjs.js int timestamp representation of the date to check
 * @returns {boolean} - Whether or not the date is inside the designated AY window.
 */
const isCurrentEducationPeriod = (
  educationPeriod,
  educationPeriodOptions,
  dateToCheck = dayjs(),
) => {
  const chosenEducationPeriod = retrieveEducationPeriod(
    educationPeriod,
    educationPeriodOptions,
  );

  if (!chosenEducationPeriod) {
    return false;
  }

  const chosenPeriodStart = dayjs(chosenEducationPeriod.contentAvailableAt);
  const chosenPeriodEnd = dayjs(chosenEducationPeriod.endDate);
  const dateRepresentation = dayjs(dateToCheck);

  return dateRepresentation.isBetween(chosenPeriodStart, chosenPeriodEnd);
};

/**
 * Determines whether the provided `dateToCheck` is after the content expiration for the chosen
 * education period. Note that this isn't the end date, but the content expiration date. Returns true
 * if the provided date is after the expiration date for the EP, false otherwise.
 * @param educationPeriod {int} - The numeric index that represents the academic year (i.e. 22 for 2020-2021)
 * @param educationPeriodOptions {object | list} - All known education periods in the format id -> education period map or an array
 * @param dateToCheck {string | number} - Either string or dayjs.js int timestamp representation of the date to check
 * @returns {boolean} - Whether or not the date is after the content for the AY expires
 */
const isAfterEducationPeriodExpires = (
  educationPeriod,
  educationPeriodOptions,
  dateToCheck = dayjs(),
) => {
  const chosenPeriodData = retrieveEducationPeriod(
    educationPeriod,
    educationPeriodOptions,
  );

  if (!chosenPeriodData) {
    return false;
  }

  const chosenPeriodEnd = dayjs(chosenPeriodData.endDate);
  const dateRepresentation = dayjs(dateToCheck);
  return dateRepresentation.isAfter(chosenPeriodEnd);
};

/**
 * Determines whether the provided `dateToCheck` is before the content for an education period is available.
 * Returns true if so, false otherwise.
 * @param educationPeriod {int} - The numeric index that represents the education period (i.e. 22 for 2020-2021)
 * @param educationPeriodOptions {object | list} - All known education periods in the format id -> education period map or an array
 * @param dateToCheck {string | number} - Either string or dayjs.js int timestamp representation of the date to check
 * @returns {boolean} - Whether or not the provided date is before the content is available.
 */
const isBeforeEducationPeriod = (
  educationPeriod,
  educationPeriodOptions,
  dateToCheck = dayjs(),
) => {
  const chosenPeriodData = retrieveEducationPeriod(
    educationPeriod,
    educationPeriodOptions,
  );

  if (!chosenPeriodData) {
    return false;
  }

  const chosenPeriodAvailable = dayjs(chosenPeriodData.contentAvailableAt);
  const dateRepresentation = dayjs(dateToCheck);
  return dateRepresentation.isBefore(chosenPeriodAvailable);
};

export {
  isCurrentEducationPeriod,
  isAfterEducationPeriodExpires,
  isBeforeEducationPeriod,
  retrieveEducationPeriod,
};
